import { authenticatedInstance } from '../index/index.service';
import type { CategoryReportGroup, CategoryView, IdResponse } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { CategoryReportGroup, CategoryView } from 'shared-models/browser';

export type CreateCategoryRequest = {
  name: string;
  sortOrder?: number;
  reportGroup?: CategoryReportGroup | null;
  parentCategoryPk?: number | null;
};

export type UpdateCategoryRequest = {
  name?: string;
  sortOrder?: number;
  reportGroup?: CategoryReportGroup | null;
  parentCategoryPk?: number | null;
};

class CategoriesService {
  // Architectural summary: Active categories (any employee role) for the POS menu rail + KDS grouping.
  async listForPos(tenantId: string, employeeSession: string): Promise<CategoryView[]> {
    try {
      const response = await authenticatedInstance.get<CategoryView[]>(`/categories/pos/tenant/${tenantId}`, {
        headers: { 'x-employee-session': employeeSession },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: All categories (active + inactive) for the manager category modal.
  async listForSettings(tenantId: string, employeeSession: string): Promise<CategoryView[]> {
    try {
      const response = await authenticatedInstance.get<CategoryView[]>(`/categories/tenant/${tenantId}`, {
        headers: { 'x-employee-session': employeeSession },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Creates a tenant category from the manager modal.
  async create(tenantId: string, payload: CreateCategoryRequest, employeeSession: string): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(`/categories/tenant/${tenantId}`, payload, {
        headers: { 'x-employee-session': employeeSession },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Diff-only rename / re-sort of one category.
  async update(categoryId: number, payload: UpdateCategoryRequest, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/categories/${categoryId}`, payload, {
        headers: { 'x-employee-session': employeeSession },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Drag-drop reorder — sends the new id order as one batch.
  async reorder(orderedIds: number[], employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        '/categories/reorder',
        { orderedIds },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Per-category membership delta — added/removed ids only, not full set.
  async setMembership(categoryId: number, addProductIds: number[], removeProductIds: number[], employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/categories/${categoryId}/products`,
        { addProductIds, removeProductIds },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Soft-deactivate a category (manager modal).
  async deactivate(categoryId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/categories/${categoryId}/deactivate`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Architectural summary: Reactivate a category (manager modal).
  async activate(categoryId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/categories/${categoryId}/activate`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const categoriesService = new CategoriesService();
